import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "./api";
import {
  Receipt, Sparkles, ExternalLink, Search, ChevronDown,
  Download, Video, Mail, Phone, MapPin, Globe2, Info,
} from "lucide-react";

const SERIF = { fontFamily: "'Cormorant Garamond', serif" };
const fmt = (n, c = "AUD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: c, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";

const StatusBadge = ({ status }) => {
  const map = {
    paid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    pending: "bg-amber-50 text-amber-700 ring-amber-200",
    failed: "bg-rose-50 text-rose-700 ring-rose-200",
    expired: "bg-slate-100 text-slate-600 ring-slate-200",
    unpaid: "bg-amber-50 text-amber-700 ring-amber-200",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ring-1 whitespace-nowrap ${map[status] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
      {status}
    </span>
  );
};

const TABS = [
  { id: "all", label: "All" },
  { id: "unlock_blueprint", label: "Blueprints" },
  { id: "session_booking", label: "Sessions" },
];

export default function AdminTransactions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openRow, setOpenRow] = useState(null);

  useEffect(() => {
    adminApi.get("/transactions")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.transactions.filter((r) => {
      if (tab !== "all" && r.intent !== tab) return false;
      if (statusFilter !== "all" && r.payment_status !== statusFilter) return false;
      if (q && !(`${r.client_name} ${r.client_email} ${r.product}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [data, tab, query, statusFilter]);

  const exportCsv = () => {
    const header = ["Date", "Client", "Email", "Phone", "Country", "Product", "Type",
      "Amount AUD", "Kassie net AUD", "Platform fee AUD", "Status", "Booking date", "Booking time"];
    const lines = rows.map((r) => [
      r.created_at, r.client_name, r.client_email, r.client_phone, r.client_country,
      r.product, r.intent === "unlock_blueprint" ? "Blueprint" : "Session",
      r.amount_aud, r.client_net_aud, r.platform_fee_aud, r.payment_status,
      r.booking_date, r.booking_time,
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kama-loka-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading) return <div className="p-12 text-slate-500">Loading…</div>;
  if (!data) return <div className="p-12 text-slate-500">No data.</div>;

  const t = data.totals || {};

  return (
    <div className="p-10 max-w-7xl mx-auto space-y-8" data-testid="admin-transactions">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700">transactions</p>
          <h1 className="font-serif text-4xl text-slate-900 mt-1" style={SERIF}>Payments</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every blueprint and session payment, with full client details. Refunds are handled directly in Stripe.
          </p>
        </div>
        <button
          data-testid="export-csv-btn"
          onClick={exportCsv}
          className="px-4 py-2 rounded-full border border-slate-200 text-sm text-slate-700 flex items-center gap-2 hover:bg-slate-50"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </header>

      {/* KPI cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="total revenue" value={fmt(t.gross)} sub={`${t.count_paid || 0} paid payments`} />
        <Kpi label="blueprints" value={fmt(t.blueprint_gross)} sub={`${t.blueprint_count || 0} unlocks · $44.44 each`} accent="text-amber-700" />
        <Kpi label="sessions" value={fmt(t.session_gross)} sub={`${t.session_count || 0} bookings`} accent="text-violet-700" />
        <Kpi label="kassie's net" value={fmt(t.kassie_total)} sub={`after ${fmt(t.blueprint_fee)} platform fees`} accent="text-emerald-700" />
      </section>

      {/* Toolbar */}
      <section className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-full border border-slate-200 bg-white p-1" data-testid="txn-tabs">
          {TABS.map((tb) => {
            const count = tb.id === "all" ? data.transactions.length
              : data.transactions.filter((r) => r.intent === tb.id).length;
            return (
              <button
                key={tb.id}
                data-testid={`txn-tab-${tb.id}`}
                onClick={() => { setTab(tb.id); setOpenRow(null); }}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                  tab === tb.id ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tb.label} <span className="opacity-60 text-xs ml-1">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="txn-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, product…"
            className="w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
        </div>
        <select
          data-testid="txn-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 focus:outline-none"
        >
          <option value="all">All statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="expired">Expired</option>
        </select>
      </section>

      {/* Table */}
      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-[0.22em] text-slate-500">
          <div className="col-span-4">Client</div>
          <div className="col-span-3">Product</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-1 text-right">Status</div>
          <div className="col-span-1" />
        </div>

        {rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            <Receipt className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            No payments match your filters.
          </div>
        ) : rows.map((r, i) => {
          const isOpen = openRow === r.id;
          const isBlueprint = r.intent === "unlock_blueprint";
          return (
            <div key={r.id} className="border-b border-slate-100 last:border-b-0">
              <button
                data-testid={`txn-row-${i}`}
                onClick={() => setOpenRow(isOpen ? null : r.id)}
                className={`w-full grid grid-cols-12 px-6 py-4 items-center text-left transition-colors ${isOpen ? "bg-slate-50" : "hover:bg-slate-50/50"}`}
              >
                <div className="col-span-4 min-w-0 flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs shrink-0 ${
                    isBlueprint ? "bg-amber-100 text-amber-700" : "bg-violet-100 text-violet-700"
                  }`}>
                    {isBlueprint ? <Sparkles className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5" />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-slate-900 truncate text-sm font-medium">{r.client_name}</p>
                    <p className="text-xs text-slate-400 truncate">{r.client_email || "no email captured"}</p>
                  </div>
                </div>
                <div className="col-span-3 min-w-0">
                  <p className="text-sm text-slate-700 truncate">{r.product}</p>
                  {r.booking_date && (
                    <p className="text-xs text-slate-400">{r.booking_date} · {r.booking_time}</p>
                  )}
                </div>
                <div className="col-span-2 text-xs text-slate-500">{fmtDate(r.created_at)}</div>
                <div className="col-span-1 text-right font-medium text-slate-900 tabular-nums">{fmt(r.amount_aud, r.currency)}</div>
                <div className="col-span-1 text-right"><StatusBadge status={r.payment_status} /></div>
                <div className="col-span-1 flex justify-end">
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>

              {isOpen && (
                <div className="px-6 pb-6 pt-1 bg-slate-50" data-testid={`txn-detail-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <DetailCard title="Client">
                      <DetailLine icon={Mail} value={r.client_email || "—"} />
                      <DetailLine icon={Phone} value={r.client_phone || "—"} />
                      <DetailLine icon={MapPin} value={r.client_address || "—"} />
                      <DetailLine icon={Globe2} value={r.client_country || "—"} />
                    </DetailCard>
                    <DetailCard title={isBlueprint ? "Payment split" : "Booking"}>
                      {isBlueprint ? (
                        <>
                          <KV k="Gross" v={fmt(r.amount_aud, r.currency)} />
                          <KV k="Kassie (net)" v={fmt(r.client_net_aud, r.currency)} tone="text-emerald-700" />
                          <KV k="Platform fee" v={fmt(r.platform_fee_aud, r.currency)} tone="text-violet-700" />
                        </>
                      ) : (
                        <>
                          <KV k="Date" v={r.booking_date || "—"} />
                          <KV k="Time" v={r.booking_time || "—"} />
                          <KV k="Format" v={r.booking_format === "in_person" ? "In person" : "Online video"} />
                          <KV k="To Kassie" v="100 %" tone="text-emerald-700" />
                        </>
                      )}
                    </DetailCard>
                    <DetailCard title="Payment">
                      <KV k="Status" v={<StatusBadge status={r.payment_status} />} />
                      <KV k="Paid at" v={fmtDate(r.completed_at)} />
                      <p className="text-[11px] text-slate-400 font-mono break-all mt-1">{r.session_id}</p>
                      {r.stripe_url ? (
                        <a
                          href={r.stripe_url}
                          target="_blank" rel="noreferrer"
                          data-testid={`txn-stripe-link-${i}`}
                          className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#635BFF] hover:underline"
                        >
                          View in Stripe <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      ) : (
                        <p className="mt-2 text-xs text-slate-400">Stripe link available once paid.</p>
                      )}
                    </DetailCard>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </section>

      <p className="text-xs text-slate-400 flex items-center gap-1.5">
        <Info className="w-3.5 h-3.5" />
        Refunds, disputes and payouts are managed directly in the Stripe dashboard — open any payment above via "View in Stripe".
      </p>
    </div>
  );
}

const Kpi = ({ label, value, sub, accent = "text-slate-900" }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6">
    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
    <p className={`mt-3 font-serif text-3xl ${accent}`} style={SERIF}>{value}</p>
    <p className="mt-1 text-xs text-slate-500">{sub}</p>
  </div>
);

const DetailCard = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2">{title}</p>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const DetailLine = ({ icon: Icon, value }) => (
  <p className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
    <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
    <span className="truncate">{value}</span>
  </p>
);

const KV = ({ k, v, tone = "text-slate-900" }) => (
  <div className="flex items-center justify-between gap-2 text-sm">
    <span className="text-slate-500">{k}</span>
    <span className={`font-medium ${tone}`}>{v}</span>
  </div>
);
